/* ==========================================================================
   FILE: modules/mind/components/GoalList.jsx
   IAMONEAI - Goal List Component
   Displays and manages user goals with status tracking
   ========================================================================== */

import React, { useState, useMemo, useCallback } from 'react';
import { Button } from '../../../global/components';
import './GoalList.css';

// ==========================================================================
// GOAL CONFIGURATION
// ==========================================================================

const GOAL_STATUSES = {
  pending: { label: 'In Progress', icon: '⏳', color: '#F59E0B' },
  done: { label: 'Completed', icon: '✅', color: '#10B981' },
  paused: { label: 'Paused', icon: '⏸️', color: '#6B7280' },
  abandoned: { label: 'Abandoned', icon: '❌', color: '#EF4444' },
  overdue: { label: 'Overdue', icon: '⚠️', color: '#EF4444' },
};

const GOAL_CATEGORIES = {
  career: { label: 'Career', icon: '💼', color: '#3B82F6' },
  finance: { label: 'Finance', icon: '💰', color: '#10B981' },
  health: { label: 'Health', icon: '💪', color: '#EF4444' },
  education: { label: 'Education', icon: '📚', color: '#8B5CF6' },
  relationship: { label: 'Relationships', icon: '❤️', color: '#EC4899' },
  family: { label: 'Family', icon: '👨‍👩‍👧‍👦', color: '#F59E0B' },
  travel: { label: 'Travel', icon: '✈️', color: '#06B6D4' },
  home: { label: 'Home', icon: '🏠', color: '#84CC16' },
  gifts: { label: 'Gifts', icon: '🎁', color: '#F97316' },
  personal: { label: 'Personal', icon: '🌟', color: '#A855F7' },
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All Goals' },
  { value: 'pending', label: '⏳ In Progress' },
  { value: 'done', label: '✅ Completed' },
  { value: 'paused', label: '⏸️ Paused' },
];

// ==========================================================================
// GOAL LIST COMPONENT
// ==========================================================================

/**
 * Displays a list of goals with filtering and status management
 * 
 * @param {Object} props
 * @param {Array} goals - Array of goal objects from memory
 * @param {Function} onGoalClick - Called when a goal is clicked
 * @param {Function} onStatusChange - Called to change goal status
 * @param {Function} onRefresh - Called to refresh goals
 * @param {boolean} loading - Loading state
 */
function GoalList({
  goals = [],
  onGoalClick,
  onStatusChange,
  onRefresh,
  loading = false,
  className = '',
}) {
  // State
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);

  // ==========================================================================
  // FILTERING & SORTING
  // ==========================================================================

  const filteredGoals = useMemo(() => {
    let filtered = [...goals];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(g => g.goal_data?.status === statusFilter);
    } else if (!showCompleted) {
      // Hide completed by default unless showing all
      filtered = filtered.filter(g => 
        g.goal_data?.status !== 'done' && g.goal_data?.status !== 'abandoned'
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(g => g.goal_data?.category === categoryFilter);
    }

    // Sort: overdue first, then by deadline, then by creation
    filtered.sort((a, b) => {
      const aData = a.goal_data || {};
      const bData = b.goal_data || {};
      
      // Completed/abandoned at the end
      const aComplete = aData.status === 'done' || aData.status === 'abandoned';
      const bComplete = bData.status === 'done' || bData.status === 'abandoned';
      if (aComplete !== bComplete) return aComplete ? 1 : -1;

      // Overdue first
      const aOverdue = isOverdue(aData.deadline);
      const bOverdue = isOverdue(bData.deadline);
      if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;

      // By deadline
      if (aData.deadline && bData.deadline) {
        return new Date(aData.deadline) - new Date(bData.deadline);
      }
      if (aData.deadline) return -1;
      if (bData.deadline) return 1;

      // By creation date
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

    return filtered;
  }, [goals, statusFilter, categoryFilter, showCompleted]);

  // Stats
  const stats = useMemo(() => {
    const active = goals.filter(g => g.goal_data?.status === 'pending').length;
    const completed = goals.filter(g => g.goal_data?.status === 'done').length;
    const overdue = goals.filter(g => 
      g.goal_data?.status === 'pending' && isOverdue(g.goal_data?.deadline)
    ).length;
    return { active, completed, overdue, total: goals.length };
  }, [goals]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleMarkDone = useCallback((goal, e) => {
    e.stopPropagation();
    onStatusChange?.(goal, 'done');
  }, [onStatusChange]);

  const handlePause = useCallback((goal, e) => {
    e.stopPropagation();
    const newStatus = goal.goal_data?.status === 'paused' ? 'pending' : 'paused';
    onStatusChange?.(goal, newStatus);
  }, [onStatusChange]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  const listClasses = [
    'goal-list',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={listClasses}>
      {/* Header */}
      <div className="goal-list-header">
        <div className="goal-list-title-row">
          <h3 className="goal-list-title">
            <TargetIcon />
            Goals
          </h3>
          {onRefresh && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              loading={loading}
              aria-label="Refresh goals"
            >
              <RefreshIcon />
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="goal-stats">
          <div className="goal-stat">
            <span className="goal-stat-value">{stats.active}</span>
            <span className="goal-stat-label">Active</span>
          </div>
          <div className="goal-stat">
            <span className="goal-stat-value goal-stat-success">{stats.completed}</span>
            <span className="goal-stat-label">Done</span>
          </div>
          {stats.overdue > 0 && (
            <div className="goal-stat">
              <span className="goal-stat-value goal-stat-warning">{stats.overdue}</span>
              <span className="goal-stat-label">Overdue</span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="goal-list-filters">
          <select
            className="goal-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_FILTERS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            className="goal-filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            {Object.entries(GOAL_CATEGORIES).map(([key, config]) => (
              <option key={key} value={key}>
                {config.icon} {config.label}
              </option>
            ))}
          </select>

          <label className="goal-show-completed">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            <span>Show completed</span>
          </label>
        </div>
      </div>

      {/* Goal List */}
      <div className="goal-list-content">
        {loading ? (
          <div className="goal-list-loading">
            <LoadingSpinner />
            <span>Loading goals...</span>
          </div>
        ) : filteredGoals.length === 0 ? (
          <div className="goal-list-empty">
            <EmptyGoalIcon />
            <p>
              {goals.length === 0 
                ? 'No goals yet. Tell me what you want to achieve!'
                : 'No goals match your filters'}
            </p>
          </div>
        ) : (
          <div className="goal-items">
            {filteredGoals.map(goal => (
              <GoalItem
                key={goal.id || goal.memoryId}
                goal={goal}
                onClick={() => onGoalClick?.(goal)}
                onMarkDone={(e) => handleMarkDone(goal, e)}
                onPause={(e) => handlePause(goal, e)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Quick Add Hint */}
      <div className="goal-list-hint">
        <LightbulbIcon />
        <span>Say "I want to..." in chat to create a new goal</span>
      </div>
    </div>
  );
}

// ==========================================================================
// GOAL ITEM COMPONENT
// ==========================================================================

function GoalItem({ goal, onClick, onMarkDone, onPause }) {
  const goalData = goal.goal_data || {};
  const status = goalData.status || 'pending';
  const category = goalData.category || 'personal';
  const deadline = goalData.deadline;
  const deadlineText = goalData.deadline_text;
  
  const statusConfig = GOAL_STATUSES[status] || GOAL_STATUSES.pending;
  const categoryConfig = GOAL_CATEGORIES[category] || GOAL_CATEGORIES.personal;
  
  const isOverdueGoal = status === 'pending' && isOverdue(deadline);
  const isComplete = status === 'done' || status === 'abandoned';

  // Format deadline display
  const deadlineDisplay = useMemo(() => {
    if (!deadline) return null;
    
    const date = new Date(deadline);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays <= 7) return `Due in ${diffDays} days`;
    
    return deadlineText || date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }, [deadline, deadlineText]);

  const itemClasses = [
    'goal-item',
    `goal-item-${status}`,
    isOverdueGoal && 'goal-item-overdue',
    isComplete && 'goal-item-complete',
  ].filter(Boolean).join(' ');

  return (
    <div className={itemClasses} onClick={onClick}>
      {/* Category Icon */}
      <div 
        className="goal-item-category"
        style={{ backgroundColor: categoryConfig.color }}
        title={categoryConfig.label}
      >
        {categoryConfig.icon}
      </div>

      {/* Content */}
      <div className="goal-item-content">
        <p className="goal-item-text">
          {goal.summary || goal.content}
        </p>
        
        <div className="goal-item-meta">
          {/* Status Badge */}
          <span 
            className="goal-item-status"
            style={{ color: statusConfig.color }}
          >
            {statusConfig.icon} {statusConfig.label}
          </span>

          {/* Deadline */}
          {deadlineDisplay && (
            <span className={`goal-item-deadline ${isOverdueGoal ? 'goal-item-deadline-overdue' : ''}`}>
              <CalendarIcon />
              {deadlineDisplay}
            </span>
          )}

          {/* Context */}
          {goal.context_id && (
            <span className="goal-item-context">
              {goal.context_id === 'personal' && '🏠'}
              {goal.context_id === 'work' && '💼'}
              {goal.context_id === 'family' && '👨‍👩‍👧‍👦'}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      {!isComplete && (
        <div className="goal-item-actions">
          <button
            className="goal-action-btn goal-action-done"
            onClick={onMarkDone}
            title="Mark as done"
          >
            <CheckIcon />
          </button>
          <button
            className="goal-action-btn goal-action-pause"
            onClick={onPause}
            title={status === 'paused' ? 'Resume' : 'Pause'}
          >
            {status === 'paused' ? <PlayIcon /> : <PauseIcon />}
          </button>
        </div>
      )}

      {/* Completed Badge */}
      {status === 'done' && goalData.completed_at && (
        <div className="goal-completed-badge">
          <CheckCircleIcon />
          <span>Completed {formatRelativeTime(goalData.completed_at)}</span>
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// GOAL DETAIL MODAL
// ==========================================================================

export function GoalDetailModal({ goal, onClose, onStatusChange }) {
  if (!goal) return null;

  const goalData = goal.goal_data || {};
  const categoryConfig = GOAL_CATEGORIES[goalData.category] || GOAL_CATEGORIES.personal;
  const statusConfig = GOAL_STATUSES[goalData.status] || GOAL_STATUSES.pending;

  return (
    <div className="goal-detail-overlay" onClick={onClose}>
      <div className="goal-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="goal-detail-header">
          <div 
            className="goal-detail-category"
            style={{ backgroundColor: categoryConfig.color }}
          >
            {categoryConfig.icon}
          </div>
          <div className="goal-detail-info">
            <h3>{categoryConfig.label} Goal</h3>
            <span 
              className="goal-detail-status"
              style={{ color: statusConfig.color }}
            >
              {statusConfig.icon} {statusConfig.label}
            </span>
          </div>
          <button className="goal-detail-close" onClick={onClose}>×</button>
        </div>

        <div className="goal-detail-content">
          <p className="goal-detail-text">{goal.content}</p>

          {goalData.deadline && (
            <div className="goal-detail-deadline">
              <CalendarIcon />
              <span>
                Deadline: {goalData.deadline_text || new Date(goalData.deadline).toLocaleDateString()}
              </span>
            </div>
          )}

          <div className="goal-detail-dates">
            <span>Created: {formatDate(goal.created_at)}</span>
            {goalData.completed_at && (
              <span>Completed: {formatDate(goalData.completed_at)}</span>
            )}
          </div>
        </div>

        <div className="goal-detail-actions">
          {goalData.status === 'pending' && (
            <>
              <Button
                variant="primary"
                onClick={() => onStatusChange?.(goal, 'done')}
              >
                <CheckIcon /> Mark Complete
              </Button>
              <Button
                variant="secondary"
                onClick={() => onStatusChange?.(goal, 'paused')}
              >
                <PauseIcon /> Pause
              </Button>
            </>
          )}
          {goalData.status === 'paused' && (
            <Button
              variant="primary"
              onClick={() => onStatusChange?.(goal, 'pending')}
            >
              <PlayIcon /> Resume
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// HELPER FUNCTIONS
// ==========================================================================

function isOverdue(deadline) {
  if (!deadline) return false;
  return new Date(deadline) < new Date();
}

function formatRelativeTime(dateString) {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function formatDate(dateString) {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

// ==========================================================================
// ICONS
// ==========================================================================

function TargetIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="6" y="4" width="4" height="16" />
      <rect x="14" y="4" width="4" height="16" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
    </svg>
  );
}

function EmptyGoalIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <div className="loading-spinner">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 12 12"
            to="360 12 12"
            dur="1s"
            repeatCount="indefinite"
          />
        </path>
      </svg>
    </div>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default GoalList;
export { GoalItem, GOAL_STATUSES, GOAL_CATEGORIES };